import React, { useEffect } from 'react';

/**
 * Two-column modal overlay.
 * Left panel: campaign banner image + org info (from modal config).
 * Right panel: step content (children).
 *
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   config: object,
 *   themeVars: object,
 *   children: React.ReactNode
 * }} props
 */
export function Modal({ isOpen, onClose, config, themeVars, children }) {
  const { Visual } = config;
  const modal = config.Modal || {};
  const organizationName = config.OrganizationName;

  const handleOverlayClose = (event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const handleOverlayKeyDown = (event) => {
    if (event.target !== event.currentTarget) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClose();
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const hasBanner = !!modal.BannerImageUrl;
  const primaryColor = Visual?.PrimaryColor || '#3D52D5';

  const bannerStyle = hasBanner
    ? { backgroundImage: `url(${modal.BannerImageUrl})` }
    : { background: `linear-gradient(160deg, ${primaryColor}cc, ${primaryColor})` };

  return (
    <div
      className="tdw-overlay"
      role="dialog"
      aria-modal="true"
      tabIndex={0}
      style={themeVars}
      onClick={handleOverlayClose}
      onKeyDown={handleOverlayKeyDown}
    >
      <div className="tdw-modal">

        {/* LEFT PANEL */}
        <div className="tdw-panel-left">
          <div className="tdw-panel-left-banner" style={bannerStyle} />

          <div className="tdw-panel-left-info">
            {(modal.LogoUrl || organizationName) && (
              <div className="tdw-panel-left-logo">
                {modal.LogoUrl && (
                  <img src={modal.LogoUrl} alt={organizationName || ''} />
                )}
                {organizationName && (
                  <span className="tdw-panel-left-org">{organizationName}</span>
                )}
              </div>
            )}

            {modal.Title && (
              <h2 className="tdw-panel-left-title">{modal.Title}</h2>
            )}

            {modal.Description && (
              <p className="tdw-panel-left-desc">{modal.Description}</p>
            )}
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="tdw-panel-right">
          {children}
        </div>

      </div>
    </div>
  );
}
